// services/api.js
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import CONFIG from "../config/config";
import { authenticatedFetch, publicFetch } from "../utils/authenticatedFetch";
import { loadSettings, saveSettings } from '../utils/settings';
import { computeBandsSeries, backtestStrategy, FIT_HISTORY_DAYS } from '../shared/bandSignals';
import { loadChartSettings, rangeDaysFor, rangeLabelFor } from '../utils/bollingerChartSettings';
import { stopKOfWatch } from '../utils/strategyTabs';
import { buildFilterTree } from '../utils/listUrl';
// Same-month year-over-year traffic. The detail panel renders the same pair
// this derives the column's percentage from, so the two surfaces cannot
// disagree about which two months they compared.
import { buildTrafficYoY } from '../utils/webTraffic';
import { marketHoursInterval } from '../utils/marketRefresh';
import { useState, useEffect } from 'react';

// TanStack Query timing (staleTime / gcTime / refetchInterval), in ms. One definition per duration
// instead of a hand-multiplied product at every hook (issue #307).
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const CACHE_MS = {
  MIN_1: MINUTE,
  MIN_2: 2 * MINUTE,
  MIN_5: 5 * MINUTE,
  MIN_30: 30 * MINUTE,
  HOUR_1: HOUR,
  HOUR_4: 4 * HOUR,
};

// Helper functions
const isValidDate = (date) => !isNaN(Date.parse(date));

const estimateNextEarningsDate = (lastEarningsDate) => {
  if (!lastEarningsDate) return null;
  const lastDate = new Date(lastEarningsDate);
  return new Date(lastDate.setMonth(lastDate.getMonth() + 3))
    .toISOString()
    .split("T")[0];
};

// Consolidated screen data fetch
export const fetchScreenData = async (screen, userEmail, customSymbols = null, filters = {}) => {
  let url = `${CONFIG.API_URLS.SCREEN_DATA}`;
  const params = new URLSearchParams();

  if (customSymbols) {
    if (Array.isArray(customSymbols) && customSymbols.length > 0) {
      params.append('symbols', customSymbols.join(','));
    } else {
      console.warn('Invalid or empty customSymbols:', customSymbols);
      return [];
    }
  } else if (screen) {
    params.append('screen', screen);
    // No userEmail param: screen_query derives identity for the watchlist screen from the
    // session token carried by authenticatedFetch below (CLAUDE.md — identity from the token,
    // never a request-supplied ?email=).
  } else {
    throw new Error('Either screen or symbols parameter is required');
  }

  // Add market cap filter parameters if present
  if (filters.marketCap && filters.marketCap.length > 0) {
    // Import inside the block to avoid circular dependency on initial load
    const { MARKET_CAP_TIERS } = require('../components/MarketCapFilter');
    const marketCapRanges = filters.marketCap
      .map((tierId) => {
        const tier = MARKET_CAP_TIERS.find((t) => t.id === tierId);
        return tier ? { min: tier.min, max: tier.max } : null;
      })
      .filter(Boolean);

    if (marketCapRanges.length > 0) {
      const finiteMins = marketCapRanges
        .map((range) => (Number.isFinite(range.min) ? range.min : null))
        .filter((value) => value !== null);

      if (finiteMins.length > 0) {
        params.append('minMarketCap', Math.min(...finiteMins));
      }

      const hasUnlimitedMax = marketCapRanges.some(
        (range) => range.max === null || !Number.isFinite(range.max)
      );

      if (!hasUnlimitedMax) {
        const finiteMaxes = marketCapRanges
          .map((range) => (Number.isFinite(range.max) ? range.max : null))
          .filter((value) => value !== null);

        if (finiteMaxes.length > 0) {
          params.append('maxMarketCap', Math.max(...finiteMaxes));
        }
      }
    }
  }

  // Add sectors filter if present; screen_query expands sectors to their industries
  if (filters.sectors && filters.sectors.length > 0) {
    params.append('sectors', filters.sectors.join(','));
  }

  // Add security type filter if present
  if (filters.types && filters.types.length > 0) {
    // Import inside the block to avoid circular dependency on initial load
    const { SECURITY_TYPES } = require('../components/TypeFilter');
    // Expand filter IDs to actual quote_type values (e.g., FUND -> MUTUALFUND, MONEYMARKET)
    const quoteTypes = filters.types
      .flatMap((typeId) => {
        const secType = SECURITY_TYPES.find((t) => t.id === typeId);
        return secType ? secType.quoteTypes : [];
      });
    if (quoteTypes.length > 0) {
      params.append('quoteTypes', quoteTypes.join(','));
    }
  }

  url += `?${params.toString()}`;
  
  // Decide whether to use publicFetch or authenticatedFetch
  // For example, only use authenticatedFetch for watchlist if userEmail is present.
  let fetchFn = publicFetch;
  if (screen === "watchlist" && userEmail) {
    fetchFn = authenticatedFetch;
  }

  try {
    const response = await fetchFn(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const screenData = await response.json();
    console.log("Raw API response:", screenData);
    console.log("Raw screen data:", screenData);

    if (!screenData || !screenData.stocks || screenData.stocks.length === 0) {
      console.error("No stocks data found in the API response");
      return [];
    }

    // Process the data
    const processedData = screenData.stocks.map((stock) => {
      let categories = [];
      try {
        if (typeof stock.category_id === 'string') {
          // If it's a JSON string, parse it
          categories = JSON.parse(stock.category_id);
        } else if (Array.isArray(stock.category_id)) {
          // Already an array
          categories = stock.category_id;
        } else if (stock.category_id) {
          // Fallback for any other non-null value
          categories = [stock.category_id];
        }
      } catch (e) {
        console.error('Error processing categories for', stock.symbol, e);
        categories = stock.category_id ? [stock.category_id] : [];
      }

      // Reshaped once per stock so the column's percentage and the visit count
      // beside it come from the SAME pair of months. Reading the newest
      // collected month here instead would put a Jul-2026 visit count next to a
      // Dec-2025-vs-Dec-2024 percentage — the mislabelling this feature exists
      // to remove (issue #122).
      const trafficYoY = buildTrafficYoY(stock.trafficData);

    //  console.log('Processed categories for', stock.symbol, {
      //  rawCategory: stock.category_id,
        //processedCategories: categories
      //});

      // Deduplicate signals by name, keeping the strongest one
      const uniqueSignals = stock.technical_signals?.reduce((acc, signal) => {
        const existingSignal = acc.find(s => s.name === signal.name);
        if (!existingSignal || existingSignal.strength < signal.strength) {
          // Remove existing signal if any
          const filtered = acc.filter(s => s.name !== signal.name);
          // Add the new (or stronger) signal
          return [...filtered, signal];
        }
        return acc;
      }, []) || [];

      return {
        name: stock.companyName,
        tickerSymbol: stock.symbol,
        website: stock.website,
        fullTimeEmployees: stock.fullTimeEmployees,
        industry: stock.industry,
        overallRisk: stock.overallRisk,
        description: stock.description,
        earningsDate: stock.next_earnings_date,
        isEarningsDateEstimated: stock.next_earnings_date_estimated === 1,
        stockPrice: stock.current_price != null && !isNaN(stock.current_price)
          ? parseFloat(stock.current_price)
          : null,
        // Backs the table's "Since Earnings" column (columnConfig key
        // stockChange) and the row-highlight rules that key on it. Now the
        // live figure — quote vs the close on the last recorded report day —
        // where it used to be current_price_cache.percent_change, frozen at
        // the last run of a worker that stopped running (issue #212).
        // Despite the name this is NOT today's change: App.js ranks top
        // movers on regularMarketChangePercent and must not fall back here.
        stockChange: stock.since_earnings_percent != null
          ? parseFloat(stock.since_earnings_percent)
          : null,
        positions: stock.glassdoor_job_count ?? null,
        positionsPercentage: stock.glassdoor_job_count && stock.fullTimeEmployees
          ? (stock.glassdoor_job_count / stock.fullTimeEmployees) * 100
          : null,
        outlook: stock.glassdoor_business_outlook_rating ?? null,
        outlookDelta: stock.glassdoor_prev_business_outlook_rating != null && stock.glassdoor_business_outlook_rating != null
          ? stock.glassdoor_business_outlook_rating - stock.glassdoor_prev_business_outlook_rating
          : null,
        glassdoorAiSummary: stock.glassdoor_ai_summary ?? null,
        glassdoorAiSentiment: stock.glassdoor_ai_sentiment ?? null,
        glassdoorOverallRating: stock.glassdoor_overall_rating ?? null,
        glassdoorCeoName: stock.glassdoor_ceo_name ?? null,
        glassdoorCeoPhoto: stock.glassdoor_ceo_photo ?? null,
        glassdoorReviewCount: stock.glassdoor_review_count ?? null,
        hasGlassdoorMatch: !!stock.glassdoor_name, // True if Glassdoor company match exists
        logo_url: stock.logo_url || null,
        trafficData: stock.trafficData || [],
        trafficChange: trafficYoY?.latestPair?.changePct ?? null,
        // Visits in the most recent COMPARABLE month — the one trafficChange is
        // measured against, not the most recent collected month.
        trafficComparableVisits: trafficYoY?.latestPair?.current ?? null,
        trafficComparablePeriod: trafficYoY?.latestPair?.periodLabel ?? null,
        lastEarningsDate: stock.last_earnings_date,
        // The move on the last report, computed by the worker from ONE
        // earnings_history row (issue #212). The chart's earnings-marker gap %
        // reads this rather than re-deriving it from a price pair, so the badge
        // and the event it sits on cannot describe different quarters.
        //
        // The two raw closes stay in the worker's payload but are deliberately
        // NOT mapped here: nothing in src/ reads them any more, and an unread
        // mapping is exactly the dead plumbing postMarketChange turned out to
        // be. Add them back at the point something renders them.
        earningsReactionDate: stock.earnings_reaction_date ?? null,
        earningsReactionPercent: stock.earnings_reaction_percent != null
          ? parseFloat(stock.earnings_reaction_percent)
          : null,
        ai_analysis: stock.ai_analysis || null,
        // New fields from current_price_cache
        regularMarketChange: stock.regular_market_change,
        regularMarketChangePercent: stock.regular_market_change_percent != null
          ? parseFloat(stock.regular_market_change_percent)
          : null,
        regularMarketTime: stock.regular_market_time,
        regularMarketDayHigh: stock.regular_market_day_high,
        regularMarketDayLow: stock.regular_market_day_low,
        regularMarketVolume: stock.regular_market_volume,
        regularMarketPreviousClose: stock.regular_market_previous_close,
        preMarketPrice: stock.pre_market_price,
        preMarketChange: stock.pre_market_change,
        preMarketChangePercent: stock.pre_market_change_percent,
        postMarketPrice: stock.post_market_price,
        // postMarketChange is gone: post_market_change has no writer in the
        // repo, so it was NULL on 7,198 of 8,724 rows and stale where it was
        // not. It was plumbed to here and rendered by nothing (issue #212).
        postMarketChangePercent: stock.post_market_change_percent,
        fiftyTwoWeekLow: stock.fifty_two_week_low,
        fiftyTwoWeekHigh: stock.fifty_two_week_high,
        fiftyTwoWeekChangePercent: stock.fifty_two_week_change_percent,
        averageVolume3Month: stock.average_volume_3month,
        averageVolume10Day: stock.average_volume_10day,
        fiftyDayAverage: stock.fifty_day_average,
        fiftyDayAverageChange: stock.fifty_day_average_change,
        fiftyDayAverageChangePercent: stock.fifty_day_average_change_percent,
        twoHundredDayAverage: stock.two_hundred_day_average,
        twoHundredDayAverageChange: stock.two_hundred_day_average_change,
        twoHundredDayAverageChangePercent: stock.two_hundred_day_average_change_percent,
        fiftyTwoWeekLowChange: stock.fifty_two_week_low_change,
        fiftyTwoWeekLowChangePercent: stock.fifty_two_week_low_change_percent,
        fiftyTwoWeekHighChange: stock.fifty_two_week_high_change,
        fiftyTwoWeekHighChangePercent: stock.fifty_two_week_high_change_percent,
        // Expectations Bar (issue #98) — how much is already priced in going
        // into earnings. Descriptive, not directional; weights uncalibrated.
        expectationsBar: stock.expectations_bar_score,
        expectationsBarRating: stock.expectations_bar_rating,
        expectationsBarCoverage: stock.expectations_bar_coverage,
        impliedMovePct: stock.implied_move_pct,
        // Fields from stock_financial_info
        currency: stock.currency,
        quoteType: stock.quote_type,
        shortName: stock.short_name,
        longName: stock.long_name,
        marketCap: stock.market_cap,
        bookValue: stock.book_value,
        sharesOutstanding: stock.shares_outstanding,
        averageAnalystRating: stock.average_analyst_rating,
        forwardPE: stock.forward_pe,
        trailingPE: stock.trailing_pe,
        priceToBook: stock.price_to_book,
        trailingAnnualDividendRate: stock.trailing_annual_dividend_rate,
        trailingAnnualDividendYield: stock.trailing_annual_dividend_yield,
        epsTrailingTwelveMonths: stock.eps_trailing_twelve_months,
        epsForward: stock.eps_forward,
        trailingPegRatio: stock.trailing_peg_ratio,
        dividendRate: stock.dividend_rate,
        dividendYield: stock.dividend_yield,
        revenuePerEmployee: stock.revenue_per_employee != null
          ? parseFloat(stock.revenue_per_employee)
          : (stock.market_cap && stock.price_to_sales_trailing_12_months && stock.fullTimeEmployees
            ? Math.round((stock.market_cap / stock.price_to_sales_trailing_12_months) / stock.fullTimeEmployees / 1000)
            : null),
        financialInfoLastUpdated: stock.financial_info_last_updated,
        // Financial Stats fields
        financialStatsLastUpdated: stock.financial_stats_last_updated,
        shortPercentOfFloat: stock.short_percent_of_float != null ? parseFloat(stock.short_percent_of_float) : null,
        enterpriseToRevenue: stock.enterprise_to_revenue != null ? parseFloat(stock.enterprise_to_revenue) : null,
        enterpriseToEbitda: stock.enterprise_to_ebitda != null ? parseFloat(stock.enterprise_to_ebitda) : null,
        pegRatio: stock.peg_ratio != null ? parseFloat(stock.peg_ratio) : null,
        quickRatio: stock.quick_ratio != null ? parseFloat(stock.quick_ratio) : null,
        currentRatio: stock.current_ratio != null ? parseFloat(stock.current_ratio) : null,
        grossMargins: stock.gross_margins != null ? parseFloat(stock.gross_margins) : null,
        operatingMargins: stock.operating_margins != null ? parseFloat(stock.operating_margins) : null,
        totalCashPerShare: stock.total_cash_per_share != null ? parseFloat(stock.total_cash_per_share) : null,
        beta: stock.beta != null ? parseFloat(stock.beta) : null,
        alpha_12m: stock.alpha_12m != null ? parseFloat(stock.alpha_12m) : null,
        priceToSalesTrailing12Months: stock.price_to_sales_trailing_12_months != null ?
          parseFloat(stock.price_to_sales_trailing_12_months) : null,
        trailingEps: stock.trailing_eps != null ? parseFloat(stock.trailing_eps) : null,
        forwardEps: stock.forward_eps != null ? parseFloat(stock.forward_eps) : null,
        
        // Also keep the original field names for compatibility
        enterprise_to_revenue: stock.enterprise_to_revenue,
        enterprise_to_ebitda: stock.enterprise_to_ebitda,
        quick_ratio: stock.quick_ratio,
        current_ratio: stock.current_ratio,
        total_cash_per_share: stock.total_cash_per_share,
        price_to_sales_trailing_12_months: stock.price_to_sales_trailing_12_months,
        trailing_eps: stock.trailing_eps,
        forward_eps: stock.forward_eps,
        
        // Add technical signals processing before the debug section
        technicalSignals: uniqueSignals,
        
        // Computed properties for quick access
        latestSignal: stock.technical_signals?.[0] || null,
        hasBuySignals: stock.technical_signals?.some(s => s.type.toUpperCase() === 'BUY') || false,
        hasSellSignals: stock.technical_signals?.some(s => s.type.toUpperCase() === 'SELL') || false,
        
        // Keep the original field names from the API
        technical_signals: stock.technical_signals || [],
        
        // Add debug logging
        ...(process.env.NODE_ENV === 'development' && {
          _debug_margins: {
            gross: stock.gross_margins,
            operating: stock.operating_margins
          }
        }),
        
        // Update category handling to support arrays
        categories,
        category_id: categories[0] || null, // For backward compatibility, use first category
        
        // Add these fields
        revenueGrowth: stock.revenue_growth != null ? parseFloat(stock.revenue_growth) : null,
        revenue_growth: stock.revenue_growth, // Keep original field name for compatibility

        // Historical price changes
        priceChange3d: stock.price_change_3d != null ? parseFloat(stock.price_change_3d) : null,
        priceChange7d: stock.price_change_7d != null ? parseFloat(stock.price_change_7d) : null,
        priceChange14d: stock.price_change_14d != null ? parseFloat(stock.price_change_14d) : null,
        priceChange1m: stock.price_change_1m != null ? parseFloat(stock.price_change_1m) : null,
        priceChange3m: stock.price_change_3m != null ? parseFloat(stock.price_change_3m) : null,
      };
    });

    console.log(`Processed screen data for ${screen || 'custom symbols'}:`, processedData);
    return processedData;
  } catch (error) {
    console.error(`Error fetching screen data for ${screen || 'custom symbols'}:`, error);
    throw error;
  }
};

const parseWatchlistSymbols = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      // Fall back to comma separated parsing
    }

    return trimmed
      .split(',')
      .map((symbol) => symbol.trim())
      .filter(Boolean);
  }

  return [];
};

export const useWatchlistData = (email) => {
  return useQuery({
    queryKey: ["watchlistData", email],
    queryFn: async () => {
      if (!email) return [];
      try {
        // First, fetch user's watchlist symbols
        const userResponse = await authenticatedFetch(
          `${CONFIG.API_URLS.USER_DATA}?userId=${email}`
        );

        if (!userResponse.ok) {
          throw new Error(`HTTP error! status: ${userResponse.status}`);
        }

        const userData = await userResponse.json();
        console.log('Raw user data:', userData);

        // Get server symbols
        const serverSymbols = parseWatchlistSymbols(userData?.companiesSymbols);
        
        // Get local symbols
        const localSymbols = parseWatchlistSymbols(localStorage.getItem('companiesSymbols'));

        // Merge symbols
        const mergedSymbols = Array.from(new Set([...serverSymbols, ...localSymbols]));

        if (!mergedSymbols.length) {
          console.log('No watchlist symbols found');
          return {
            companiesSymbols: [],
            companies: [],
            preferences: userData.preferences || {}
          };
        }

        // Fetch stock data for merged symbols
        const stockData = await fetchScreenData('watchlist', email, mergedSymbols);
        console.log('Fetched stock data for watchlist:', stockData);

        return {
          companiesSymbols: mergedSymbols,
          companies: stockData,
          preferences: userData.preferences || {}
        };
      } catch (error) {
        console.error('Error fetching watchlist data:', error);
        return {
          companiesSymbols: [],
          companies: [],
          preferences: {}
        };
      }
    },
    enabled: !!email,
    retry: 3,
    retryDelay: 1000,
  });
};

export const updateWatchlist = async (user, symbols) => {
  try {
    if (!user?.email) {
      throw new Error('User email is required');
    }

    if (!Array.isArray(symbols)) {
      throw new Error('Symbols must be an array');
    }

    console.log('Updating watchlist:', {
      email: user.email,
      symbolsCount: symbols.length
    });

    const response = await authenticatedFetch(CONFIG.API_URLS.USER_DATA, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: user.email,
        email: user.email,
        companiesSymbols: symbols,
        firstUseTimestamp: localStorage.getItem('firstUseTimestamp') || new Date().toISOString()
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Watchlist update failed:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Watchlist update response:', data);
    return data;
  } catch (error) {
    console.error('Error updating watchlist:', error);
    throw error;
  }
};

export const useEconomicData = (geography) => {
  return useQuery({
    queryKey: ["economicData", geography],
    queryFn: async () => {
      const url = `${CONFIG.API_URLS.ECONOMIC_CALENDAR}?timeframe=-1,0,1&geography=${geography}`;
      console.log("Fetching economic data from:", url);
      try {
        const response = await publicFetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log("Economic data response:", data); // Debug log

        if (!data || typeof data !== "object") {
          throw new Error("Invalid economic data received");
        }

        // Ensure we return the expected structure
        return {
          events: data.events || {}, // Events should be an object with dates as keys
          analysis: data.analysis || null // Analysis should be the analysis object or null
        };
      } catch (error) {
        console.error(`Error fetching economic data:`, error.message);
        throw error;
      }
    },
    staleTime: CACHE_MS.MIN_5,
    cacheTime: CACHE_MS.MIN_30, // Keep unused data in cache for 30 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useMarketAnalysis = () => {
  return useQuery({
    queryKey: ['marketAnalysis'],
    queryFn: async () => {
      // No query params: the worker reads neither a date nor a refresh flag, it
      // always serves the newest analysis it holds for the current UTC day.
      const response = await authenticatedFetch(CONFIG.API_URLS.MARKET_NEWS);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Raw Market Analysis API Response:', data);
      return data;
    },
    refetchInterval: CACHE_MS.HOUR_1,
    staleTime: CACHE_MS.HOUR_1,
    cacheTime: 2 * 60 * 60 * 1000, // Keep unused data in cache for 2 hours
    // A 503 here means the worker has no analysis at all; hammering it three
    // more times per page load cannot change that.
    retry: 1,
  });
};

const fetchEconomicData = async (geography = "US") => {
  const url = `${CONFIG.API_URLS.ECONOMIC_CALENDAR}?timeframe=-1,0,1&geography=${geography}`;
  console.log("Fetching economic data from:", url);
  try {
    const response = await axios.get(url);
    if (!response.data || typeof response.data !== "object") {
      throw new Error("Invalid economic data received");
    }
    return response.data;
  } catch (error) {
    console.error(`Error fetching economic data:`, error.message);
    throw error;
  }
};

const normalizeSymbolsInput = (input) => {
  if (input === undefined) {
    return undefined;
  }

  if (input === null) {
    return [];
  }

  if (Array.isArray(input)) {
    return input;
  }

  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    } catch (error) {
      // Fall through to comma-splitting if JSON.parse fails
    }

    return trimmed
      .split(',')
      .map((symbol) => symbol.trim())
      .filter(Boolean);
  }

  return [];
};

export const updateUserData = async (user, data = {}) => {
  try {
    const isStringUser = typeof user === 'string';
    const userId = !user
      ? null
      : isStringUser
        ? user
        : user.id || user.email || null;
    const emailString = isStringUser
      ? user
      : typeof user?.email === 'object'
        ? user.email?.email
        : user?.email || userId;

    if (!emailString) {
      throw new Error('User email is required to update user data');
    }

    // Remove lists from preferences before sending them to the server
    const { lists, ...otherPreferences } = data.preferences || {};

    const normalizedSymbols = normalizeSymbolsInput(data.companiesSymbols);

    const payload = {
      email: emailString,
      ...(userId ? { userId } : {}),
      ...(data.name !== undefined ? { name: data.name } : {}),
      ...(data.firstUseTimestamp !== undefined ? { firstUseTimestamp: data.firstUseTimestamp } : {}),
      ...(data.preferences !== undefined ? { preferences: otherPreferences } : {}),
      ...(normalizedSymbols !== undefined ? { companiesSymbols: normalizedSymbols } : {}),
      ...(data.stockNotes !== undefined ? { stockNotes: data.stockNotes } : {}),
    };

    console.log('Sending user data payload:', {
      ...payload,
      companiesSymbols: undefined,
      companiesSymbolsCount: Array.isArray(payload.companiesSymbols)
        ? payload.companiesSymbols.length
        : undefined,
    });

    const response = await authenticatedFetch(CONFIG.API_URLS.USER_DATA, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (_) {
        errorData = { error: `HTTP ${response.status}` };
      }
      console.error('Server error response:', errorData);
      throw new Error(errorData.error || 'Failed to update user data');
    }

    const result = await response.json();
    if (result.id) {
      localStorage.setItem('userId', result.id);
    }
    return result;
  } catch (error) {
    console.error('Error updating user data:', error);
    throw error;
  }
};

// Improved createUserList combining best of both versions
export const createUserList = async (listData) => {
  try {
    console.log('Creating list with data:', listData);
    
    if (!listData) {
      throw new Error('List data is required');
    }
    
    if (!listData.name) {
      throw new Error('List name is required');
    }
    
    if (!listData.owner_id) {
      throw new Error('Owner ID is required');
    }
    
    // Format data with proper validation
    const formattedData = {
      ...listData,
      name: listData.name || 'Unnamed List',
      description: listData.description || '',
      tooltip: listData.tooltip || '',
      type: listData.type || 'custom',
      // Fix the symbols formatting to prevent escaping characters
      symbols: Array.isArray(listData.symbols) ? 
        JSON.stringify(listData.symbols).replace(/\\\"/g, '"') : 
        (listData.symbols || '[]'),
      prompt: listData.prompt || '',
      // Include filter_config if provided
      filter_config: listData.filter_config || '',
      icon: listData.icon || 'list',
      category_id: listData.category_id || 'personal',
      is_public: listData.is_public === true,
      owner_id: listData.owner_id,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Add more detailed logging to debug issues
    console.log('Creating user list with formatted data:', {
      name: formattedData.name,
      type: formattedData.type,
      symbolsCount: Array.isArray(listData.symbols) ? listData.symbols.length : 'Unknown',
      hasFilterConfig: !!formattedData.filter_config
    });

    const response = await authenticatedFetch(CONFIG.API_URLS.USER_LISTS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formattedData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create list');
    }

    const savedList = await response.json();
    
    // Return parsed data
    return {
      ...savedList,
      symbols: typeof savedList.symbols === 'string' ? 
        JSON.parse(savedList.symbols) : 
        savedList.symbols
    };
  } catch (error) {
    console.error('Error creating list:', error);
    throw error;
  }
};

export const shareUserList = async (listId, userId, accessType = 'view') => {
  try {
    const response = await authenticatedFetch(`${CONFIG.API_URLS.USER_LISTS}/${listId}/share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId, accessType })
    });

    if (!response.ok) {
      throw new Error('Failed to share list');
    }

    return await response.json();
  } catch (error) {
    console.error('Error sharing list:', error);
    throw error;
  }
};

export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      try {
        const response = await publicFetch(`${CONFIG.API_URLS.SCREEN_QUERY}/available-screens`);

        if (!response.ok) {
          console.error('Response not ok. Status:', response.status, response.statusText);
          const text = await response.text();
          console.error('Body content:', text);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const categories = await response.json();

        // 1) Post-process each category to unify short_description with description
        categories.forEach(category => {
          if (category.short_description) {
            // Overwrite description with short_description if it exists
            category.description = category.short_description;
          }

          // 2) Do the same for each filter
          if (category.filters) {
            category.filters.forEach(filter => {
              if (filter.short_description) {
                filter.description = filter.short_description;
              }
            });
          }
        });

        // Process the response to include parent-child relationships
        categories.forEach(category => {
          if (category.filters) {
            // Nest filters under their parent_list at any depth; only top-level ones (and
            // orphans whose parent isn't in this category) stay in the main array
            category.filters = buildFilterTree(category.filters);
          }
        });

        return categories;
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        throw error;
      }
    },
    staleTime: CACHE_MS.MIN_2, // refresh more often for new thesis lists
    cacheTime: CACHE_MS.MIN_30,
    refetchOnWindowFocus: true, // Refresh when user tabs back
  });
};

export const useAIFilter = () => {
  return useMutation({
    mutationFn: async (prompt) => {
      const response = await publicFetch('https://api.1stprinciples.pro/ai-filter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        throw new Error('Failed to process AI filter request');
      }

      return response.json();
    },
  });
};

export const saveScreenerData = async (screenerData) => {
  const response = await fetch('https://api.1stprinciples.pro/screen', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(screenerData),
  });

  if (!response.ok) {
    throw new Error('Failed to save screener data');
  }

  return response.json();
};

// Add new list management functions
export const updateUserList = async (listId, listData) => {
  try {
    const response = await authenticatedFetch(`${CONFIG.API_URLS.USER_LISTS}/${listId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(listData)
    });

    if (!response.ok) {
      throw new Error('Failed to update list');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating list:', error);
    throw error;
  }
};

// Improved useUserLists combining best of both versions
export const useUserLists = (email) => {
  const queryClient = useQueryClient();

  const { data: userLists, ...rest } = useQuery({
    queryKey: ['userLists', email],
    queryFn: async () => {
      if (!email) return [];
      
      try {
        const response = await authenticatedFetch(CONFIG.API_URLS.USER_LISTS);
        if (!response.ok) throw new Error('Failed to fetch user lists');
        
        const lists = await response.json();
        
        // Parse JSON fields for all lists
        const processedLists = lists.map(list => ({
          ...list,
          symbols: typeof list.symbols === 'string' ? JSON.parse(list.symbols) : list.symbols,
          ...(list.type === 'portfolio' && {
            sector_analysis: list.sector_analysis ? JSON.parse(list.sector_analysis) : null,
            risk_metrics: list.risk_metrics ? JSON.parse(list.risk_metrics) : null,
            suggested_symbols: list.suggested_symbols ? JSON.parse(list.suggested_symbols) : null
          })
        }));

        // Add debugging about any duplicates
        const nameOccurrences = {};
        processedLists.forEach(list => {
          const nameKey = `${list.name}-${list.type}`;
          if (!nameOccurrences[nameKey]) {
            nameOccurrences[nameKey] = [];
          }
          nameOccurrences[nameKey].push(list.id);
        });
        
        const duplicates = Object.entries(nameOccurrences)
          .filter(([_, ids]) => ids.length > 1);
          
        if (duplicates.length > 0) {
          console.log('Found duplicate lists in API response:', duplicates);
        }

        // Update local settings
        saveSettings('lists', processedLists);
        return processedLists;
      } catch (error) {
        console.error('Error fetching user lists:', error);
        // Fallback to local settings on error
        return loadSettings().lists;
      }
    },
    enabled: !!email,
    staleTime: CACHE_MS.MIN_5,
    cacheTime: CACHE_MS.MIN_30,
    initialData: () => loadSettings().lists,
    retry: 3,
    retryDelay: 1000
  });

  // Mutation for creating lists
  const createList = useMutation({
    mutationFn: async (listData) => {
      const response = await authenticatedFetch(CONFIG.API_URLS.USER_LISTS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(listData)
      });

      if (!response.ok) {
        throw new Error('Failed to create list');
      }

      return response.json();
    },
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: ['userLists', email] });
      const currentSettings = loadSettings();
      saveSettings('lists', [...currentSettings.lists, newList]);
    }
  });

  // Mutation for deleting lists
  const deleteList = useMutation({
    mutationFn: async (listId) => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.USER_LISTS}/${listId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete list');
      }

      return response.json();
    },
    onSuccess: (_, deletedListId) => {
      queryClient.invalidateQueries({ queryKey: ['userLists', email] });
      const currentSettings = loadSettings();
      saveSettings('lists', currentSettings.lists.filter(list => list.id !== deletedListId));
    }
  });

  return {
    data: userLists,
    createList,
    deleteList,
    ...rest
  };
};

// Add delete list function
export const deleteUserList = async (listId) => {
  try {
    const response = await authenticatedFetch(`${CONFIG.API_URLS.USER_LISTS}/${listId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error('Failed to delete list');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting list:', error);
    throw error;
  }
};

// Fetch industry list from the API
export const useIndustryList = () => {
  const [industries, setIndustries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(CONFIG.API_URLS.SCREEN_QUERY_INDUSTRIES);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch industries: ${response.status}`);
        }
        
        const data = await response.json();

        const formattedIndustries = Array.isArray(data)
          ? data
              .filter(industry => industry?.id)
              .map(industry => ({
                id: industry.id,
                label: industry.label || industry.id,
                count: industry.count || 0
              }))
          : [];

        formattedIndustries.sort((a, b) => b.count - a.count);

        setIndustries(formattedIndustries);
        setError(null);
      } catch (err) {
        console.error('Error fetching industry list:', err);
        setError(err.message);
        setIndustries([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchIndustries();
  }, []);

  return { industries, isLoading, error };
};

// Fetch market cap counts from the API
export const useMarketCapCounts = () => {
  const [marketCapCounts, setMarketCapCounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMarketCapCounts = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(CONFIG.API_URLS.SCREEN_QUERY_MARKET_CAP_COUNTS);

        if (!response.ok) {
          throw new Error(`Failed to fetch market cap counts: ${response.status}`);
        }

        const data = await response.json();
        setMarketCapCounts(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        console.error('Error fetching market cap counts:', err);
        setError(err.message);
        setMarketCapCounts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarketCapCounts();
  }, []);

  return { marketCapCounts, isLoading, error };
};

// Fetch security type counts from the API
export const useSecurityTypes = () => {
  const [securityTypes, setSecurityTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSecurityTypes = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(CONFIG.API_URLS.SCREEN_QUERY_SECURITY_TYPES);

        if (!response.ok) {
          throw new Error(`Failed to fetch security types: ${response.status}`);
        }

        const data = await response.json();
        setSecurityTypes(Array.isArray(data) ? data : []);
        setError(null);
      } catch (err) {
        console.error('Error fetching security types:', err);
        setError(err.message);
        setSecurityTypes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSecurityTypes();
  }, []);

  return { securityTypes, isLoading, error };
};

/**
 * Fetches custom filter results from the API
 * @param {string} filterId - The ID of the custom filter
 * @param {string} userId - The ID or email of the user who owns the filter
 * @returns {Promise<Array<string>>} - Array of stock symbols matching the filter criteria
 */
export const fetchCustomFilterResults = async (filterId, userId) => {
  try {
    console.log('Fetching custom filter results for filter:', filterId, 'and user:', userId);
    
    const response = await publicFetch(CONFIG.API_URLS.CUSTOM_FILTER, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filter_id: filterId,
        user_id: userId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Custom filter API error:', errorData);
      throw new Error(errorData.error || 'Failed to fetch custom filter results');
    }

    const data = await response.json();
    console.log('Custom filter results received:', data);
    return data.symbols || [];
  } catch (error) {
    console.error('Error fetching custom filter results:', error);
    throw error; // Let the component handle displaying the error
  }
};

// Notification subscription functions
export const toggleListNotification = async (listId, subscribe) => {
  try {
    const response = await authenticatedFetch(
      `${CONFIG.API_URLS.USER_DATA.replace('/user-data', '/notification-subscriptions')}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listId, subscribe })
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error toggling list notification:', error);
    throw error;
  }
};

export const useNotificationSubscriptions = (email) => {
  return useQuery({
    queryKey: ['notificationSubscriptions', email],
    queryFn: async () => {
      if (!email) return [];

      try {
        const response = await authenticatedFetch(
          `${CONFIG.API_URLS.USER_DATA.replace('/user-data', '/notification-subscriptions')}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.lists || [];
      } catch (error) {
        console.error('Error fetching notification subscriptions:', error);
        return [];
      }
    },
    enabled: !!email,
    staleTime: CACHE_MS.MIN_5,
  });
};

// Generated band-strategy signals: recent events + open positions with P&L
// (read-only view over what signal-watch-cron persists; digest-shaped payload)
export const useSignals = (enabled = true, email, days = 30) => {
  return useQuery({
    queryKey: ['signals', email || 'me', days],
    queryFn: async () => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.SIGNALS}?days=${days}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json(); // { as_of, days, strategies, watches, events, positions, closed_trades }
    },
    enabled,
    staleTime: CACHE_MS.MIN_5,
    // Issue #396: an open tab keeps the strategies panel current through the trading day.
    refetchInterval: marketHoursInterval,
  });
};

// Signal-watch notifications (per-stock band-strategy alerts)
export const useSignalWatches = (enabled = true, email) => {
  return useQuery({
    queryKey: ['signalWatches', email || 'me'],
    queryFn: async () => {
      const response = await authenticatedFetch(CONFIG.API_URLS.SIGNAL_WATCHES);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json(); // { watches, count, max, positionCap, positionCapDefault, positionCapConfigured, openPositions }
    },
    enabled,
    staleTime: CACHE_MS.MIN_2,
  });
};

// Portfolio cap (issue #154): how many of a strategy's watches may hold a
// position AT ONCE — per strategy since #322. Distinct from the watch cap,
// which limits how many tickers can be watched at all. Sending null restores
// the server default rather than meaning "zero". The cap is read from BOTH the
// signals payload (strategies[].position_cap) and the watches endpoint, so a
// save invalidates both.
// Since #352 the same PATCH also carries `allowShort` (whether short-shaped
// watches may enter); only the keys present in the body are written.
export const useUpdateStrategy = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ strategyId, ...fields }) => {
      const response = await authenticatedFetch(CONFIG.API_URLS.SIGNAL_WATCHES, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ strategyId, ...fields }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signalWatches'] });
      queryClient.invalidateQueries({ queryKey: ['signals'] });
    },
  });
};

// Broker connection (issue #347): the owner's Tradier account, read through the
// broker-tradier worker. A 403 means the viewer is not the account owner — the
// panel renders no broker block at all rather than an error, so `eligible`
// carries that distinction. Balances/positions are live reads, never cached
// server-side, so a short staleTime keeps the panel honest without hammering
// the broker on every re-render.
export const useBrokerStatus = (enabled = true, email) => {
  return useQuery({
    queryKey: ['broker', 'status', email || 'me'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.BROKER}/status`);
      if (response.status === 403) return { eligible: false };
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return { eligible: true, ...(await response.json()) };
    },
    enabled,
    staleTime: CACHE_MS.MIN_1,
    retry: false,
  });
};

// off | sandbox | live. The worker answers with the resulting status, which is
// written straight into the cache so the panel flips without a second round trip.
export const useSetBrokerMode = (email) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mode) => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.BROKER}/mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP error! status: ${response.status}`);
      return { eligible: true, ...body };
    },
    onSuccess: (status) => {
      queryClient.setQueryData(['broker', 'status', email || 'me'], status);
    },
  });
};

// Allocation share (1–100% of account equity the strategy may deploy); the
// worker answers with the resulting status, written straight into the cache.
export const useSetBrokerConfig = (email) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (config) => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.BROKER}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || `HTTP error! status: ${response.status}`);
      return { eligible: true, ...body };
    },
    onSuccess: (status) => {
      queryClient.setQueryData(['broker', 'status', email || 'me'], status);
    },
  });
};

// One request for many tickers' daily bars — the historical-prices worker
// accepts a comma-separated symbols list. Rows with null/zero closes are
// dropped per ticker; they NaN every rolling computation downstream.
// FIT_HISTORY_DAYS = 1Y of backtest window + longest-SMA warmup (the same
// budget the Trading Range chart and the server refit use).
export const fetchHistoricalPricesBatch = async (symbols, days = FIT_HISTORY_DAYS) => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);
  const start = startDate.toISOString().slice(0, 10);
  const response = await fetch(
    `${CONFIG.API_URLS.HISTORICAL_PRICES}?symbols=${encodeURIComponent(symbols.join(','))}&start_date=${start}`
  );
  if (!response.ok) throw new Error(`Historical prices fetch failed: ${response.status}`);
  const json = await response.json();
  const out = {};
  for (const sym of symbols) {
    out[sym] = (json.data?.[sym] || []).filter((p) => Number.isFinite(p.close) && p.close > 0);
  }
  return out;
};

// Backtest stats for each signal watch at its SAVED fit (shape + period),
// shown in the notifications dialog. One batched price request for all
// watched tickers; the math is the chart's own backtestStrategy AND each
// watch is scored over its own saved window (lookback_days) — the range a
// watched stock's Trading Range chart opens on — so the dialog's numbers match
// what the chart shows when you open that stock — including the watch's
// strategy stop (issue #417; `strategies` from GET /api/signal-watches). Returns
// { [ticker]: { trades, wins, totalReturn, rangeLabel } }.
export const useWatchBacktests = (watches, enabled = true, strategies = []) => {
  const stopKOf = (w) => stopKOfWatch(strategies, w);
  // The watch's OWN window (issue #472): a watched stock's chart now opens on
  // the watched fit, range included, so that is what the chart shows. Falls
  // back to the chart's per-stock range for a length no range offers.
  const windowOf = (w) => {
    const label = rangeLabelFor(Number(w.lookback_days)) || loadChartSettings(w.ticker).range;
    return { days: rangeDaysFor(label), label };
  };
  // Key on the full fit + range + stop, not just tickers — stats change when a
  // watch's shape/period is updated, the chart range for a stock changes or
  // its strategy's stop does, even though the ticker set didn't
  const fitKey = watches
    .map((w) => `${w.ticker}|${w.strategy}|${w.period}|${windowOf(w).days}|${stopKOf(w)}`)
    .sort()
    .join(',');
  return useQuery({
    queryKey: ['watchBacktests', fitKey],
    queryFn: async () => {
      const prices = await fetchHistoricalPricesBatch([...new Set(watches.map((w) => w.ticker))]);
      const stats = {};
      for (const w of watches) {
        const rows = prices[w.ticker];
        if (!rows?.length) continue;
        const { days, label: rangeLabel } = windowOf(w);
        const series = computeBandsSeries(rows, w.period);
        const { trades, wins, totalReturn } = backtestStrategy(series, w.strategy, w.period, days, { adverseK: stopKOf(w) });
        stats[w.ticker] = { trades, wins, totalReturn, rangeLabel };
      }
      return stats;
    },
    enabled: enabled && watches.length > 0,
    staleTime: CACHE_MS.HOUR_1, // daily bars — no need to recompute within the hour
  });
};

// lookbackDays = the chart range the fit was ranked over (issue #339); the
// worker stores it beside the fit so "has this fit's window turned over" is
// answerable server-side.
export const saveSignalWatch = async ({ ticker, period, strategy, lookbackDays }) => {
  const response = await authenticatedFetch(CONFIG.API_URLS.SIGNAL_WATCHES, {
    method: 'POST',
    body: JSON.stringify({ ticker, period, strategy, lookbackDays }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw Object.assign(new Error(data.error || `HTTP error! status: ${response.status}`), { code: data.code, status: response.status });
  }
  return data;
};

export const removeSignalWatch = async (ticker) => {
  const response = await authenticatedFetch(CONFIG.API_URLS.SIGNAL_WATCHES, {
    method: 'DELETE',
    body: JSON.stringify({ ticker }),
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

export const useWebhookConfig = (enabled = true, email) => {
  return useQuery({
    queryKey: ['webhookConfig', email || 'me'],
    queryFn: async () => {
      const response = await authenticatedFetch(CONFIG.API_URLS.WEBHOOK_CONFIG);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json(); // { configured, url, secret, enabled, failure_count, ... }
    },
    enabled,
    staleTime: CACHE_MS.MIN_5,
  });
};

export const saveWebhookConfig = async (url) => {
  const response = await authenticatedFetch(CONFIG.API_URLS.WEBHOOK_CONFIG, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
  return data;
};

export const deleteWebhookConfig = async () => {
  const response = await authenticatedFetch(CONFIG.API_URLS.WEBHOOK_CONFIG, { method: 'DELETE' });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return await response.json();
};

export const testWebhook = async () => {
  const response = await authenticatedFetch(CONFIG.API_URLS.WEBHOOK_TEST, { method: 'POST' });
  return await response.json().catch(() => ({ success: false, error: `HTTP ${response.status}` }));
};

// Token-in-URL feeds (calendar ICS, signals JSON) share mint/rotate semantics:
// GET mints the token on first use and returns { url }; POST rotates it (the old URL stops working).
const useTokenFeedQuery = (queryKey, apiUrl, enabled, email) => {
  return useQuery({
    queryKey: [queryKey, email || 'me'],
    queryFn: async () => {
      const response = await authenticatedFetch(apiUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json(); // { url }
    },
    enabled,
    staleTime: CACHE_MS.HOUR_1,
  });
};

const rotateTokenFeed = async (apiUrl) => {
  const response = await authenticatedFetch(apiUrl, { method: 'POST' });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
  return data;
};

export const useCalendarFeed = (enabled = true, email) => useTokenFeedQuery('calendarFeed', CONFIG.API_URLS.CALENDAR_FEED, enabled, email);
export const regenerateCalendarFeed = () => rotateTokenFeed(CONFIG.API_URLS.CALENDAR_FEED);

export const useSignalsFeed = (enabled = true, email) => useTokenFeedQuery('signalsFeed', CONFIG.API_URLS.SIGNALS_FEED, enabled, email);
export const regenerateSignalsFeed = () => rotateTokenFeed(CONFIG.API_URLS.SIGNALS_FEED);

// ---- Web push subscriptions (browser/device notifications) ----

export const usePushDevices = (enabled = true, email) => {
  return useQuery({
    queryKey: ['pushDevices', email || 'me'],
    queryFn: async () => {
      const response = await authenticatedFetch(CONFIG.API_URLS.PUSH);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json(); // { devices: [{ endpoint, ua, created_at, last_success_at }] }
    },
    enabled,
    staleTime: CACHE_MS.MIN_5,
  });
};

// POST to a push endpoint, returning parsed JSON and throwing on non-2xx.
const pushPost = async (path, body) => {
  const response = await authenticatedFetch(`${CONFIG.API_URLS.PUSH}${path}`, {
    method: 'POST',
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `HTTP error! status: ${response.status}`);
  return data;
};

export const savePushSubscription = (subscription) => pushPost('/subscribe', { subscription });
export const removePushSubscription = (endpoint) => pushPost('/unsubscribe', { endpoint });
export const sendPushTest = () => pushPost('/test'); // { sent, failed, pruned }

// Favorite lists functions
export const toggleFavoriteList = async (listId, favorite) => {
  try {
    const response = await authenticatedFetch(
      `${CONFIG.API_URLS.USER_DATA.replace('/user-data', '/favorite-lists')}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listId, favorite })
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error toggling favorite list:', error);
    throw error;
  }
};

// Batch add favorites (for onboarding wizard)
export const addFavoriteLists = async (listIds) => {
  try {
    const response = await authenticatedFetch(
      `${CONFIG.API_URLS.USER_DATA.replace('/user-data', '/favorite-lists')}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listIds, action: 'add' })
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[ONBOARDING] Error adding favorite lists:', error);
    throw error;
  }
};

export const useFavoriteLists = (email) => {
  return useQuery({
    queryKey: ['favoriteLists', email],
    queryFn: async () => {
      if (!email) return [];

      try {
        const response = await authenticatedFetch(
          `${CONFIG.API_URLS.USER_DATA.replace('/user-data', '/favorite-lists')}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.lists || [];
      } catch (error) {
        console.error('Error fetching favorite lists:', error);
        return [];
      }
    },
    enabled: !!email,
    staleTime: CACHE_MS.MIN_5,
  });
};

// List performance hook - fetches daily performance for all lists
/**
 * Asset-class crowding read (`/fear-greed/buckets`, issue #369). ONE hook and ONE query key for
 * the desktop "Asset classes" tab and the mobile Brief cards, so the two cannot disagree. The
 * route is itself cached 15 min. `enabled` lets App.js skip the request on desktop, where the
 * Brief is never built.
 */
export const useAssetBuckets = ({ enabled = true } = {}) => useQuery({
  queryKey: ['assetBuckets'],
  queryFn: async () => {
    const response = await publicFetch('https://api.1stprinciples.pro/fear-greed/buckets');
    if (!response.ok) throw new Error(`Asset buckets request failed: ${response.status}`);
    return response.json();
  },
  enabled,
  staleTime: CACHE_MS.MIN_5,
  retry: 1,
});

/**
 * Headline market quotes (S&P 500, Nasdaq, Bitcoin, gold) for the desktop Market Analysis
 * header (`/fear-greed/markets`, issue #413). The route is edge-cached 60s; refetches every
 * 2 min only while the tab is visible (TanStack's default, no background refetch).
 */
export const useHeadlineMarkets = () => useQuery({
  queryKey: ['headlineMarkets'],
  queryFn: async () => {
    const response = await publicFetch('https://api.1stprinciples.pro/fear-greed/markets');
    if (!response.ok) throw new Error(`Headline markets request failed: ${response.status}`);
    return response.json();
  },
  staleTime: CACHE_MS.MIN_1,
  refetchInterval: CACHE_MS.MIN_2,
  retry: 1,
});

/**
 * Guidance raised / kept / lowered per earnings season (issue #387). ONE hook and ONE query key
 * for the desktop Market Analysis "Guidance" card and the mobile Brief card; the route is public
 * and edge-cached 30 min, and both surfaces pick and word the season through
 * `src/shared/guidanceSeason.js`.
 */
// The stock profile's Competition table (issue #445): the AI-named competitors'
// own screen rows, in ONE batched request per profile. Keyed on the sorted
// ticker list so two profiles naming the same rivals share the cache entry.
export const useCompetitorProfiles = (tickers) => {
  const key = [...tickers].sort().join(',');
  return useQuery({
    queryKey: ['competitorProfiles', key],
    queryFn: () => fetchScreenData(null, null, key.split(',')),
    enabled: tickers.length > 0,
    staleTime: CACHE_MS.MIN_30,
    gcTime: CACHE_MS.HOUR_1,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const useGuidanceSeason = ({ enabled = true } = {}) => useQuery({
  queryKey: ['guidanceSeason'],
  queryFn: async () => {
    const response = await publicFetch(`${CONFIG.API_URLS.EARNINGS_HIGHLIGHTS}/guidance-season`);
    if (!response.ok) throw new Error(`Guidance season request failed: ${response.status}`);
    return response.json();
  },
  enabled,
  staleTime: CACHE_MS.MIN_30,
  gcTime: CACHE_MS.HOUR_4,
  refetchOnWindowFocus: false,
  retry: 1,
});

export const useListPerformance = () => {
  return useQuery({
    queryKey: ['listPerformance'],
    queryFn: async () => {
      try {
        const response = await publicFetch(`${CONFIG.API_URLS.SCREEN_QUERY}/list-performance`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error('Error fetching list performance:', error);
        return {};
      }
    },
    staleTime: CACHE_MS.MIN_30, // matches backend cache
    gcTime: CACHE_MS.HOUR_1, // TanStack Query v5: cacheTime was renamed gcTime and is silently ignored
    refetchOnWindowFocus: false,
    refetchInterval: marketHoursInterval, // issue #396
  });
};

// Performance badge for a list whose symbols the public map cannot cover (issue #335 /
// #337): the watchlist (server-resolved from the session, GET ?screen=watchlist) or a
// browser-only symbol set — strategy, label and ad-hoc tabs — whose tickers are POSTed.
// Both return ONE entry, re-keyed here under `listId` so StockTable can merge it over the
// public map and ListPerformanceSummary looks it up by the selected tab, unchanged.
// Bearer-authenticated and never edge-cached (the public route is one shared response);
// keyed on the symbols themselves so an add/remove refetches without an explicit invalidate.
export const useScopedListPerformance = (listId, email, symbols) => {
  const symbolKey = Array.isArray(symbols) ? [...symbols].sort().join(',') : '';
  const isWatchlist = listId === 'watchlist';
  return useQuery({
    queryKey: ['listPerformance', 'scoped', listId, email, symbolKey],
    queryFn: async () => {
      const url = `${CONFIG.API_URLS.SCREEN_QUERY}/list-performance${isWatchlist ? '?screen=watchlist' : ''}`;
      const response = await authenticatedFetch(url, isWatchlist ? {} : {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const entry = isWatchlist ? data.watchlist : data.symbols;
      return entry ? { [listId]: entry } : {};
    },
    enabled: !!email && !!listId && symbolKey.length > 0,
    staleTime: CACHE_MS.MIN_5,
    gcTime: CACHE_MS.MIN_30,
    refetchOnWindowFocus: false,
    refetchInterval: marketHoursInterval, // issue #396
  });
};

// Fetch Glassdoor AI summary on-demand (requires authentication)
export const fetchGlassdoorSummary = async (symbol) => {
  try {
    const response = await authenticatedFetch(`https://api.1stprinciples.pro/glassdoor-summary?symbol=${symbol}`);

    if (!response.ok) {
      if (response.status === 404) {
        return null; // No reviews available
      }
      if (response.status === 401) {
        console.warn('[GD_SUMMARY] Unauthorized - user not logged in');
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching Glassdoor summary:', error);
    return null;
  }
};

// ============================================
// Watchlist Labels (Sub-Watchlists) Functions
// ============================================

const LABELS_API_URL = CONFIG.API_URLS.USER_DATA.replace('/user-data', '/watchlist-labels');

// Hook to fetch watchlist labels
export const useWatchlistLabels = (email) => {
  return useQuery({
    queryKey: ['watchlistLabels', email],
    queryFn: async () => {
      if (!email) return { labels: [], assignments: {} };

      try {
        const response = await authenticatedFetch(LABELS_API_URL);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      } catch (error) {
        console.error('[LABELS] Error fetching watchlist labels:', error);
        return { labels: [], assignments: {} };
      }
    },
    enabled: !!email,
    staleTime: CACHE_MS.MIN_5,
  });
};

// Create a new label
export const createLabel = async ({ name, color, trackPurchases }) => {
  const response = await authenticatedFetch(LABELS_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'create_label', name, color, trackPurchases })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create label');
  }

  return response.json();
};

// Update a label
export const updateLabel = async ({ labelId, name, color, trackPurchases }) => {
  const response = await authenticatedFetch(LABELS_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'update_label', labelId, name, color, trackPurchases })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update label');
  }

  return response.json();
};

// Delete a label
export const deleteLabel = async (labelId) => {
  const response = await authenticatedFetch(LABELS_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'delete_label', labelId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete label');
  }

  return response.json();
};

// Assign a label to a stock
export const assignLabel = async ({ symbol, labelId, purchase }) => {
  const response = await authenticatedFetch(LABELS_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'assign', symbol, labelId, purchase })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to assign label');
  }

  return response.json();
};

// Unassign a label from a stock
export const unassignLabel = async ({ symbol, labelId }) => {
  const response = await authenticatedFetch(LABELS_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'unassign', symbol, labelId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to unassign label');
  }

  return response.json();
};

// Update purchase info for a label assignment
export const updatePurchaseInfo = async ({ symbol, labelId, purchase }) => {
  const response = await authenticatedFetch(LABELS_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'update_purchase', symbol, labelId, purchase })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update purchase info');
  }

  return response.json();
};

// ─── AI Portfolio Hooks ────────────────────────────────────────

export const useAIPortfolios = (email) => {
  return useQuery({
    queryKey: ['aiPortfolios', email],
    queryFn: async () => {
      if (!email) return [];
      const response = await authenticatedFetch(
        `${CONFIG.API_URLS.AI_PORTFOLIO}?userId=${email}`
      );
      if (!response.ok) throw new Error('Failed to fetch portfolios');
      return response.json();
    },
    enabled: !!email,
  });
};

export const useAIPortfolioDetail = (id) => {
  return useQuery({
    queryKey: ['aiPortfolioDetail', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.AI_PORTFOLIO}/${id}`);
      if (!response.ok) throw new Error('Failed to fetch portfolio');
      return response.json();
    },
    enabled: !!id,
  });
};

export const useAIPortfolioHoldings = (id) => {
  return useQuery({
    queryKey: ['aiPortfolioHoldings', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.AI_PORTFOLIO}/${id}/holdings`);
      if (!response.ok) throw new Error('Failed to fetch holdings');
      return response.json();
    },
    enabled: !!id,
    refetchInterval: CACHE_MS.MIN_1, // live prices
  });
};

export const useAIPortfolioTransactions = (id) => {
  return useQuery({
    queryKey: ['aiPortfolioTransactions', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.AI_PORTFOLIO}/${id}/transactions`);
      if (!response.ok) throw new Error('Failed to fetch transactions');
      return response.json();
    },
    enabled: !!id,
  });
};

export const useAIPortfolioPerformance = (id) => {
  return useQuery({
    queryKey: ['aiPortfolioPerformance', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.AI_PORTFOLIO}/${id}/performance`);
      if (!response.ok) throw new Error('Failed to fetch performance data');
      return response.json();
    },
    enabled: !!id,
    staleTime: CACHE_MS.MIN_5,
  });
};

export const useAIPortfolioMemory = (id) => {
  return useQuery({
    queryKey: ['aiPortfolioMemory', id],
    queryFn: async () => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.AI_PORTFOLIO}/${id}/memory`);
      if (!response.ok) throw new Error('Failed to fetch memory data');
      return response.json();
    },
    enabled: !!id,
    staleTime: CACHE_MS.MIN_5,
  });
};

export const useCreateAIPortfolio = () => {
  return useMutation({
    mutationFn: async (data) => {
      const response = await authenticatedFetch(CONFIG.API_URLS.AI_PORTFOLIO, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to create portfolio');
      }
      return response.json();
    },
  });
};

export const useUpdateAIPortfolio = () => {
  return useMutation({
    mutationFn: async ({ id, ...data }) => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.AI_PORTFOLIO}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update portfolio');
      return response.json();
    },
  });
};

export const useDeleteAIPortfolio = () => {
  return useMutation({
    mutationFn: async (id) => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.AI_PORTFOLIO}/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete portfolio');
      return response.json();
    },
  });
};

export const useTriggerRebalance = () => {
  return useMutation({
    mutationFn: async (id) => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.AI_PORTFOLIO}/${id}/rebalance`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to trigger rebalance');
      return response.json();
    },
  });
};

// ─── Portfolio Sharing & Power User Hooks ──────────────────────

export const usePowerUserCheck = (email) => {
  return useQuery({
    queryKey: ['powerUserCheck', email],
    queryFn: async () => {
      if (!email) return { isPowerUser: false };
      const response = await authenticatedFetch(
        `${CONFIG.API_URLS.AI_PORTFOLIO}/power-check?userId=${email}`
      );
      if (!response.ok) return { isPowerUser: false };
      return response.json();
    },
    enabled: !!email,
    staleTime: Infinity,
  });
};

export const usePublicPortfolios = (email) => {
  return useQuery({
    queryKey: ['publicPortfolios', email || '__anon__'],
    queryFn: async () => {
      const response = await publicFetch(
        `${CONFIG.API_URLS.AI_PORTFOLIO}/public${email ? `?userId=${email}` : ''}`
      );
      if (!response.ok) throw new Error('Failed to fetch public portfolios');
      return response.json();
    },
    staleTime: CACHE_MS.MIN_2,
  });
};

export const useFollowedPortfolios = (email) => {
  return useQuery({
    queryKey: ['followedPortfolios', email],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `${CONFIG.API_URLS.AI_PORTFOLIO}/followed?userId=${email}`
      );
      if (!response.ok) throw new Error('Failed to fetch followed portfolios');
      return response.json();
    },
    enabled: !!email,
  });
};

export const useFollowPortfolio = () => {
  return useMutation({
    mutationFn: async ({ email, portfolioId }) => {
      const response = await authenticatedFetch(
        `${CONFIG.API_URLS.AI_PORTFOLIO}/follow?userId=${email}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ portfolioId }),
        }
      );
      if (!response.ok) throw new Error('Failed to follow portfolio');
      return response.json();
    },
  });
};

export const useUnfollowPortfolio = () => {
  return useMutation({
    mutationFn: async ({ email, portfolioId }) => {
      const response = await authenticatedFetch(
        `${CONFIG.API_URLS.AI_PORTFOLIO}/unfollow?userId=${email}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ portfolioId }),
        }
      );
      if (!response.ok) throw new Error('Failed to unfollow portfolio');
      return response.json();
    },
  });
};

export const useDismissPortfolio = () => {
  return useMutation({
    mutationFn: async ({ email, portfolioId }) => {
      const response = await authenticatedFetch(
        `${CONFIG.API_URLS.AI_PORTFOLIO}/dismiss?userId=${email}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ portfolioId }),
        }
      );
      if (!response.ok) throw new Error('Failed to dismiss portfolio');
      return response.json();
    },
  });
};

// Color palette for labels
export const LABEL_COLORS = [
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Yellow', value: '#F59E0B' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Orange', value: '#F97316' },
];

// Deduped, upper-cased, sorted — so two components asking for the same set share one cache entry.
const tickerSetKey = (tickers) =>
  Array.from(new Set((tickers || []).filter(Boolean).map((t) => t.toUpperCase()))).sort();

/**
 * Earnings-call highlights for a set of tickers.
 *
 * Batched deliberately: both surfaces (the earnings tile and the expanded stock row) render many
 * tickers at once, and a per-ticker fetch would fan out to dozens of calls per page. The key is
 * sorted so two components asking for the same set share one cache entry rather than churning it.
 *
 * Non-power users get a map of `{locked:true, callDate, title}` sentinels rather than nothing:
 * the worker answers `gated: true` and CallHighlights renders a Pro teaser from it, so the
 * permission branch lives in one component and the five parent components stay tier-unaware.
 */
export const useCallHighlights = (tickers, email) => {
  const key = tickerSetKey(tickers);

  return useQuery({
    queryKey: ['callHighlights', email || null, key.join(',')],
    enabled: Boolean(email) && key.length > 0,
    queryFn: async () => {
      // No email in the query string: the worker derives identity from the Bearer token that
      // authenticatedFetch attaches. A user identifier in a URL is spoofable and gets logged.
      const url = `${CONFIG.API_URLS.EARNINGS_HIGHLIGHTS}?tickers=${encodeURIComponent(key.join(','))}`;
      const response = await authenticatedFetch(url);
      if (!response.ok) throw new Error(`Call highlights HTTP ${response.status}`);
      const data = await response.json();
      return data?.highlights || {};
    },
    // Short on purpose (issue #300). A call's CONTENT never changes, but this query returns the
    // ticker -> NEWEST-call mapping, which does: an absent entry appears when a call finishes, and a
    // present one is replaced by the next quarter's. An hour here hid a just-finished call on
    // in-app navigation. It is one batched request, so five minutes costs little.
    staleTime: CACHE_MS.MIN_5,
    gcTime: CACHE_MS.HOUR_4,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Guidance direction per ticker, for the stock table's Guidance column (issue #306).
 *
 * A separate slim read (`view=guidance`) rather than useCallHighlights: the column needs every row
 * on the page at once, and the full highlights payload is ~9 KB per ticker. The direction is
 * public, so this runs signed out too; signed in, the Bearer lets the worker include the detail for
 * power users, and the email in the key keeps one session's response from being served to another.
 */
export const useCallGuidance = (tickers, email) => {
  const key = tickerSetKey(tickers);

  return useQuery({
    queryKey: ['callGuidance', email || null, key.join(',')],
    enabled: key.length > 0,
    queryFn: async () => {
      const url = `${CONFIG.API_URLS.EARNINGS_HIGHLIGHTS}?view=guidance&tickers=${encodeURIComponent(key.join(','))}`;
      const response = await (email ? authenticatedFetch : publicFetch)(url);
      if (!response.ok) throw new Error(`Call guidance HTTP ${response.status}`);
      const data = await response.json();
      return data?.guidance || {};
    },
    // Five minutes: a call's content never changes, but the ticker -> newest-call mapping does when
    // a call finishes, and a long staleTime would hide it. One batched request, so this costs little.
    staleTime: CACHE_MS.MIN_5,
    gcTime: CACHE_MS.HOUR_4,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

export const EMPTY_MOVER_NEWS = Object.freeze({ session_date: null, as_of: null, stale: false, themes: [], movers: {} });

/**
 * Why watchlist stocks moved 5%+ (issue #317): themes + per-ticker explanations for the newest
 * session the mover-news worker has explained, for a set of tickers.
 *
 * Public (the worker serves only tickers you name, and the text is a search summary), so
 * `publicFetch` works logged out. One batched request per ticker set; the key is the sorted set so
 * the desktop card and the mobile Brief asking for the same watchlist share one cache entry.
 */
export const useMoverNews = (tickers) => {
  const key = tickerSetKey(tickers);

  return useQuery({
    queryKey: ['moverNews', key.join(',')],
    enabled: key.length > 0,
    queryFn: async () => {
      const url = `${CONFIG.API_URLS.MOVER_NEWS}?tickers=${encodeURIComponent(key.join(','))}`;
      const response = await publicFetch(url);
      if (!response.ok) throw new Error(`Mover news HTTP ${response.status}`);
      const data = await response.json();
      return data && typeof data === 'object' ? { ...EMPTY_MOVER_NEWS, ...data } : EMPTY_MOVER_NEWS;
    },
    // The worker runs every two hours in the session and rows only accrete, so five minutes is
    // the most a fresh explanation waits behind this cache (the edge adds up to five more).
    staleTime: CACHE_MS.MIN_5,
    gcTime: CACHE_MS.HOUR_4,
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * The earnings-call briefing email opt-in (issue #207).
 *
 * Deliberately NOT stored in `users.preferences`. That column is written as a WHOLE OBJECT by two
 * independent writers — `fp_user` replaces it outright and `App.js` posts `{...userSettings, key}`
 * from a localStorage snapshot taken at login — so a flag written here would be missing from that
 * snapshot and silently wiped the next time the user touched a filter. It lives in its own
 * `users.earnings_call_email` column, read and written through this endpoint only.
 *
 * Identity comes from the Bearer token; no email is sent in the URL.
 */
export const useCallEmailPreference = (email) => {
  return useQuery({
    queryKey: ['callEmailPreference', email || null],
    enabled: Boolean(email),
    queryFn: async () => {
      const response = await authenticatedFetch(`${CONFIG.API_URLS.EARNINGS_HIGHLIGHTS}/preferences`);
      // Throw rather than returning a default. A falsy default would be indistinguishable from a
      // real "off", and the UI would offer a switch whose position it could not actually vouch for.
      if (!response.ok) throw new Error(`Call email preference HTTP ${response.status}`);
      return response.json();
    },
    staleTime: CACHE_MS.MIN_5,
    retry: 1,
  });
};

export const setCallEmailPreference = async (enabled) => {
  const response = await authenticatedFetch(`${CONFIG.API_URLS.EARNINGS_HIGHLIGHTS}/preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled: Boolean(enabled) }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `Could not save that (HTTP ${response.status})`);
  }
  return response.json();
};
